import React, { useEffect, useMemo } from 'react'
import '../../assets/theme/theme.less'
import { LayoutComponent as Layout, ButtonComponent as Button } from '../../components'
import { Row, Col, Form, Select, Switch, InputNumber, message as alert } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { createAutomacao, resetSuccessAction } from '../../redux/slices/automacaoOperacaoSlice'
import { getProducts } from '../../redux/slices/productsSlice'
import { ReactComponent as iconChevron } from '../../assets/img/chevron-left.svg'
import { buildCronFromSchedule, DEFAULT_CRON_SCHEDULE } from '../../utils/cronSchedule'
import CronScheduleFields from './cronScheduleFields'

const { Option } = Select

const AutomacaoForm = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [form] = Form.useForm()
    const modoCotacao = Form.useWatch('modo_cotacao', form)

    const { isSubmit, successAction, automacao, message } = useSelector((state) => state.automacaoOperacao)
    const { products, isLoading: loadingProducts } = useSelector((state) => state.products)

    const eligibleProducts = useMemo(() => {
        return (products || []).filter((p) => p.contract_type != null)
    }, [products])

    useEffect(() => {
        dispatch(getProducts())
    }, [dispatch])

    useEffect(() => {
        if (message) {
            alert.error(message)
        }
    }, [message])

    useEffect(() => {
        if (successAction && automacao?.id) {
            alert.success('Automação criada com sucesso!')
            dispatch(resetSuccessAction())
            navigate(`/automacoes/${automacao.id}`)
        }
    }, [successAction, automacao, dispatch, navigate])

    const handleSubmit = async (values) => {
        try {
            await dispatch(createAutomacao({
                product_id: values.product_id,
                enabled: !!values.enabled,
                horario_cron: buildCronFromSchedule(values.dias_semana, values.horario),
                modo_cotacao: values.modo_cotacao,
                cotacao_fixa: values.modo_cotacao === 'fixa' ? values.cotacao_fixa : null,
                valor_saida_padrao: values.valor_saida_padrao ?? 0,
                percentual_diario: values.percentual_diario != null && values.percentual_diario !== ''
                    ? values.percentual_diario
                    : null
            })).unwrap()
        } catch (error) {
            alert.error(error || 'Erro ao criar automação')
        }
    }

    return (
        <Layout width>
            <Row>
                <Col md={24}>
                    <div className="d-flex justify-between align-center">
                        <div>
                            <h1 className="title page-title">Nova Automação</h1>
                            <p className="tx-gray mt-10">
                                Configure a liquidação automática diária por produto.
                            </p>
                        </div>
                        <div style={{ width: 140 }}>
                            <Button
                                type="back"
                                icon={iconChevron}
                                htmlType="button"
                                onClick={() => navigate('/automacoes')}
                            >
                                Voltar
                            </Button>
                        </div>
                    </div>
                </Col>
                <Col md={24} className="mt-25">
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                        initialValues={{
                            enabled: false,
                            dias_semana: DEFAULT_CRON_SCHEDULE.dias_semana,
                            horario: DEFAULT_CRON_SCHEDULE.horario.clone(),
                            modo_cotacao: 'fixa',
                            cotacao_fixa: 5.09,
                            valor_saida_padrao: 0,
                            percentual_diario: null
                        }}
                    >
                        <Row gutter={[16, 16]}>
                            <Col md={12}>
                                <Form.Item
                                    name="product_id"
                                    label={<span className="tx-pink font-bold">Produto</span>}
                                    rules={[{ required: true, message: 'Selecione o produto' }]}
                                >
                                    <Select
                                        placeholder="Selecione um produto"
                                        loading={loadingProducts}
                                        showSearch
                                        optionFilterProp="children"
                                    >
                                        {eligibleProducts.map((product) => (
                                            <Option key={product.id} value={product.id}>
                                                {product.title} ({product.ticker}) — tipo {product.contract_type}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col md={12}>
                                <Form.Item
                                    name="enabled"
                                    label={<span className="tx-pink font-bold">Ativar automação</span>}
                                    valuePropName="checked"
                                >
                                    <Switch checkedChildren="Ativa" unCheckedChildren="Inativa" />
                                </Form.Item>
                            </Col>
                            <CronScheduleFields />
                            <Col md={12}>
                                <Form.Item
                                    name="modo_cotacao"
                                    label={<span className="tx-pink font-bold">Modo da cotação</span>}
                                    rules={[{ required: true, message: 'Selecione o modo' }]}
                                >
                                    <Select>
                                        <Option value="fixa">Cotação fixa</Option>
                                        <Option value="api">API (dólar do dia)</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            {modoCotacao === 'fixa' && (
                                <Col md={12}>
                                    <Form.Item
                                        name="cotacao_fixa"
                                        label={<span className="tx-pink font-bold">Cotação fixa (USD/BRL)</span>}
                                        rules={[{ required: true, message: 'Informe a cotação fixa' }]}
                                    >
                                        <InputNumber min={0.0001} step={0.01} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            )}
                            <Col md={12}>
                                <Form.Item
                                    name="percentual_diario"
                                    label={<span className="tx-pink font-bold">Percentual diário (%)</span>}
                                    extra="Se informado (> 0), calcula valorSaida = totalSaidaUsd anterior × (1 + %/100) e ignora o valor saída padrão"
                                >
                                    <InputNumber min={0} step={0.0001} precision={6} style={{ width: '100%' }} placeholder="Ex.: 0.0566" />
                                </Form.Item>
                            </Col>
                            <Col md={12}>
                                <Form.Item
                                    name="valor_saida_padrao"
                                    label={<span className="tx-pink font-bold">Valor saída padrão</span>}
                                    extra="Usado apenas se o % diário estiver vazio ou zero. Use 0 para replicar saldos"
                                    rules={[{ required: true, message: 'Informe o valor de saída' }]}
                                >
                                    <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <div className="mt-25 d-flex align-center">
                            <div style={{ width: 220 }} className="mr-10">
                                <Button type="primary" active htmlType="submit" loading={isSubmit}>
                                    Criar Automação
                                </Button>
                            </div>
                            <div style={{ width: 140 }}>
                                <Button
                                    type="back"
                                    htmlType="button"
                                    onClick={() => navigate('/automacoes')}
                                >
                                    Cancelar
                                </Button>
                            </div>
                        </div>
                    </Form>
                </Col>
            </Row>
        </Layout>
    )
}

export default AutomacaoForm
